import { Link } from "wouter";
import { Sparkles, Twitter, Github, Linkedin } from "lucide-react";

import { useState, useEffect } from "react";

function ClientOnlyYear() {
  const [year, setYear] = useState<number | null>(null);

  useEffect(() => {
    if (window.__PRERENDER__) return;
    setYear(new Date().getFullYear());
  }, []);

  // Render a placeholder or nothing during SSR/Prerender
  // Rendering nothing ensures the text node structure matches if we render nothing initially
  // But to avoid layout shift, we might want a default.
  // However, for hydration, we want match.
  // If we render null here, the parent <p> will have "©  PhotoshopRequest..."
  // Client initial: "©  PhotoshopRequest..."
  // Client effect: "© 2025 PhotoshopRequest..."
  // This is safe.
  return <>{year || new Date().getFullYear()}</>;
}

export function Footer() {
  const footerLinks = {
    "Product": [
      { label: "How It Works", href: "/how-it-works" },
      { label: "Pricing", href: "/pricing" },
      { label: "Create Request", href: "/create" },
      { label: "Dashboard", href: "/dashboard" },
    ],
    "Resources": [
      { label: "Blog", href: "/blog" },
      { label: "FAQ", href: "/faq" },
      { label: "About Us", href: "/about" },
      { label: "My Posts", href: "/posts" },
    ],
    "Features": [
      { label: "Background Removal", href: "/blog/background-removal-ai-vs-manual" },
      { label: "Photo Enhancement", href: "/blog/ai-photo-editing-tricks" },
      { label: "AI Upscaling", href: "/blog/upscale-photos-without-losing-quality" },
      { label: "Color Correction", href: "/blog/color-correction-guide" },
    ],
    "Legal": [
      { label: "Terms of Service", href: "#" },
      { label: "Privacy Policy", href: "#" },
      { label: "Refund Policy", href: "#" },
      { label: "Cookie Policy", href: "#" },
    ],
  };

  return (
    <footer className="bg-background border-t border-primary/10 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="font-bold text-foreground mb-4">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors text-sm cursor-pointer block hover:translate-x-1 duration-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-border/40 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                <img
                  src="/logos/logo.webp"
                  srcSet="/logos/logo-84w.webp 84w, /logos/logo-168w.webp 168w"
                  sizes="24px"
                  width="84"
                  height="84"
                  loading="lazy"
                  alt="PhotoshopRequest.app"
                  className="h-6 w-6 object-contain"
                />
              </div>
              <span className="font-bold text-lg bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/80">
                PhotoshopRequest.app
              </span>
            </div>

            <p className="text-muted-foreground text-sm">
              © <ClientOnlyYear /> PhotoshopRequest.app. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
